import { environment } from "../../config/environment";
import { fetchText } from "../../../fetch";
import pick from "lodash/pick";
import { utc } from "moment";

import AuthService from "../AuthService";
import { fromBase64Url } from "../../../utils";
import {
  ChallengesAvailableOrSentModel,
  ChallengesReceivedModel,
  PuzzleModel,
  ChallengeStatsByDateModel,
  RespondChallengePayloadModel,
  CreateChallengePayloadModel,
} from "../../models/Challenge";
import { User } from "../../models/User";
import { UserService } from "../UserService";
import ChallengeService from "./ChallengeService";

const API_PATH = environment.FRIEND_API;
const PUZZLE_API_PATH = environment.PUZZLE_API;
const NUMBER_OF_GAMES_AVAILABLE_FOR_NON_MEMBERS = 7;

class ChallengeApiService {
  private async getAvailablePuzzles(
    gameSlug: string,
    user: User,
    gameDate?: Date
  ): Promise<PuzzleModel[]> {
    const date = gameDate ? utc(gameDate) : utc();
    const isValidMember = UserService.isValidMember(user);
    const formattedDate = ChallengeService.formatDate(date);

    const puzzleResponse = await fetchText(
      `${PUZZLE_API_PATH}/puzzle/?date=${formattedDate}&gameId=${gameSlug}`
    );
    const puzzles = JSON.parse(fromBase64Url(puzzleResponse));
    return puzzles
      .sort((a: PuzzleModel, b: PuzzleModel) =>
        a.date > b.date ? -1 : 1
      )
      .map((puzzle, i) => ({
        ...pick(puzzle, ["date", "puzzleId"]),
        ...{
          availableToPlay:
            isValidMember || i < NUMBER_OF_GAMES_AVAILABLE_FOR_NON_MEMBERS,
        },
      }));
  }

  public async getWagerSumup(gameId?: number): Promise<number> {
    return await AuthService.authFetch(
      `${API_PATH}/challenge/sumup-wagers/${gameId ? `?gameId=${gameId}` : ""}`,
      undefined,
      false
    );
  }

  public async getTotalChallengesSent(
    gameSlug: string,
    user: User
  ): Promise<ChallengeStatsByDateModel[]> {
    const puzzles = await this.getAvailablePuzzles(gameSlug, user);
    const challengeCounts = await AuthService.authFetch(
      `${API_PATH}/challenge/total-challenges-sent/`,
      {
        method: "POST",
        body: JSON.stringify({ gameIds: puzzles.map((p) => p.puzzleId) }),
      }
    );
    return puzzles.map((puzzle) => ({
      ...puzzle,
      ...(challengeCounts.find((c) => puzzle.puzzleId === c.gameId) || {}),
    }));
  }

  public async getTotalChallengesReceived(
    gameSlug: string,
    user: User
  ): Promise<ChallengeStatsByDateModel[]> {
    const puzzles = await this.getAvailablePuzzles(gameSlug, user);
    const challengeCounts = await AuthService.authFetch(
      `${API_PATH}/challenge/total-challenges-received/`,
      {
        method: "POST",
        body: JSON.stringify({ gameIds: puzzles.map((p) => p.puzzleId) }),
      }
    );
    return puzzles.map((p) => ({
      ...p,
      ...(challengeCounts.find((c) => p.puzzleId === c.gameId) || {}),
    }));
  }

  public async getFriendsAvailableAndExistingSentChallengesByGame(
    gameSlug: string,
    gameId: number,
    user: User
  ): Promise<ChallengesAvailableOrSentModel> {
    const gameInfo = (await this.getAvailablePuzzles(gameSlug, user)).find(
      (p) => p.puzzleId === gameId
    );
    const challengesSent = await AuthService.authFetch(
      `${API_PATH}/challenge/challenges-sent/${gameId}`
    );
    const friendsAvailableToChallenge = await AuthService.authFetch(
      `${API_PATH}/challenge/available-friends-to-challenge/${gameId}`,
      { method: "POST" }
    );
    return {
      gameInfo,
      friendsAvailableToChallenge: friendsAvailableToChallenge.filter(
        (f) => !f.challengeId
      ),
      challengesSent,
    };
  }

  public async getChallengesReceivedByGame(
    gameSlug: string,
    gameId: number,
    user: User
  ): Promise<ChallengesReceivedModel> {
    const gameInfo = (await this.getAvailablePuzzles(gameSlug, user)).find(
      (p) => p.puzzleId === gameId
    );
    const challengesReceived = await AuthService.authFetch(
      `${API_PATH}/challenge/challenges-received/${gameId}`
    );
    return { gameInfo, challengesReceived };
  }

  public async createChallenge(payload: CreateChallengePayloadModel): Promise<void> {
    const body = JSON.stringify(payload);
    return await AuthService.authFetch(`${API_PATH}/challenge/save-challenge`, {
      method: "POST",
      body,
    });
  }

  public async updateChallengeActivity(challengeId: any, isChallengeCreatedByUser: boolean): Promise<void> {
    const body = JSON.stringify( isChallengeCreatedByUser? { has_user_activity : false } : { has_friend_activity: false } );
    return await AuthService.authFetch(`${API_PATH}/challenge/update-challenge-activity/${challengeId}`, {
      method: "POST",
      body,
    });
  }

  public async acceptDeclineChallenge(payload: RespondChallengePayloadModel): Promise<void> {
    const body = JSON.stringify(payload);
    return await AuthService.authFetch(
      `${API_PATH}/challenge/accept-decline-challenges`,
      { method: "POST", body },
      false
    );
  }
}

export default new ChallengeApiService();
